/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */

import React, { useState, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity } from 'react-native';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import Colors from '@global/colors';
import MentionDropdown from './MentionDropdown';
import { renderContentWithMentions } from './mentionUtils';
import useCommentMentions from './useCommentMentions';

const INPUT_FONT_STYLE = {
  fontFamily: 'figtree-regular',
  fontSize: 14,
  lineHeight: 20,
};

const MAX_VISIBLE_LINES = 3;
const MAX_INPUT_HEIGHT = INPUT_FONT_STYLE.lineHeight * MAX_VISIBLE_LINES;

const CommentInput = ({
  onSubmit,
  initialValue = '',
  submitLabel = 'Send',
}) => {
  const [content, setContent] = useState(initialValue);
  const [focused, setFocused] = useState(false);
  const [inputHeight, setInputHeight] = useState(MAX_INPUT_HEIGHT);
  const [selection, setSelection] = useState(undefined);
  const [scrollOffset, setScrollOffset] = useState(0);
  const inputRef = useRef(null);

  const {
    mentions,
    mentionUsers,
    activeMentionIndex,
    mentionHasMore,
    onLoadMoreMentions,
    onMentionTextChange,
    onMentionKeyPress,
    onSelectMention,
    getUsedMentions,
    resetMentions,
  } = useCommentMentions({
    content,
    setContent,
    inputRef,
    onCursorChange: (pos) => setSelection({ start: pos, end: pos }),
  });

  const onChangeText = (value) => {
    setContent(value);
    onMentionTextChange(value);
    requestAnimationFrame(() => {
      const node = inputRef.current;
      if (
        node &&
        typeof node.scrollHeight === 'number' &&
        node.scrollHeight > node.clientHeight
      ) {
        node.scrollTop = node.scrollHeight;
        setScrollOffset(node.scrollTop);
      }
    });
  };

  const onSend = () => {
    if (!content.trim()) {
      return;
    }
    onSubmit(content.trim(), getUsedMentions(content));
    setContent('');
    resetMentions();
    setInputHeight(MAX_INPUT_HEIGHT);
    setSelection({ start: 0, end: 0 });
    setScrollOffset(0);
  };

  return (
    <View
      style={{
        position: 'relative',
        paddingHorizontal: 16,
        paddingVertical: 12,
      }}>
      <MentionDropdown
        users={mentionUsers}
        activeIndex={activeMentionIndex}
        onSelect={onSelectMention}
        hasMore={mentionHasMore}
        onLoadMore={onLoadMoreMentions}
      />
      <View
        style={{
          borderWidth: 1,
          borderColor: focused ? Colors.blue13 : Colors.grey0,
          borderRadius: 4,
          padding: 8,
        }}>
        <View
          style={{
            minWidth: 0,
            position: 'relative',
            justifyContent: 'flex-start',
          }}>
          {/* Colored overlay — purely visual, sits behind the real (transparent-text) input.
              Plain Text (not SociollaText) so it doesn't recompute its own lineHeight — it must
              match INPUT_FONT_STYLE exactly, the same object the TextInput below uses. Capped
              to the same MAX_INPUT_HEIGHT + clipped, and shifted by scrollOffset, so it stays in
              lockstep with the real TextInput once that starts scrolling internally past 3 lines. */}
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: inputHeight,
              overflow: 'hidden',
              paddingVertical: 4,
            }}>
            <Text
              style={[
                INPUT_FONT_STYLE,
                { transform: [{ translateY: -scrollOffset }] },
              ]}>
              {content ? renderContentWithMentions(content, mentions) : ''}
            </Text>
          </View>
          <TextInput
            testID="comment-input"
            ref={inputRef}
            value={content}
            onChangeText={onChangeText}
            onKeyPress={onMentionKeyPress}
            selection={selection}
            onSelectionChange={(e) => setSelection(e.nativeEvent.selection)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder="Type a comment..."
            placeholderTextColor={Colors.grey1}
            multiline
            textAlignVertical="top"
            cursorColor={Colors.black}
            onContentSizeChange={() => setInputHeight(MAX_INPUT_HEIGHT)}
            onScroll={(e) => setScrollOffset(e.nativeEvent.contentOffset.y)}
            style={{
              ...INPUT_FONT_STYLE,
              height: inputHeight,
              paddingVertical: 4,
              paddingHorizontal: 0,
              outlineStyle: 'none',
              color: Colors.transparent,
              caretColor: Colors.black,
            }}
          />
        </View>
      </View>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'flex-end',
          marginTop: 8,
        }}>
        <TouchableOpacity
          testID="comment-send-btn"
          disabled={!content.trim()}
          onPress={onSend}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 16,
            paddingRight: 16,
            borderRadius: 8,
            backgroundColor: content.trim() ? Colors.blue0 : Colors.grey0,
          }}>
          <Image
            source={require('@assets/img/icons/send-white.svg')}
            style={{ width: 16, height: 16 }}
            width={16}
            height={16}
            svg={true}
          />
          <SociollaText
            type="medium"
            style={{
              fontSize: 13,
              color: content.trim() ? Colors.white : Colors.grey1,
            }}>
            {submitLabel}
          </SociollaText>
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default CommentInput;
